/**
 * Browser Storage Controls — batched enforcement rollout DATA (Jira + cross-product web-storage keys).
 *
 * GENERATED from the atlassian-cookies `storage-items` list (product = jira or all; other
 * products' keys are left to the catch-all gate) by the `browser-storage-rollout-batches`
 * dev command. Jira strictly-necessary keys share one SN batch; the rest of the Jira keys
 * are grouped by team into a hard cap of 11 batches (the count never grows — incremental
 * runs grow existing batches). Cross-product (`product = all`) keys live in their own
 * CROSS_PRODUCT_ROLLOUT_BATCHES, a hard cap of 2 batches (the count never grows). Do not
 * hand-edit SN_ROLLOUT_BATCHES / ROLLOUT_BATCHES / CROSS_PRODUCT_ROLLOUT_BATCHES — run
 * `yarn dev browser-storage-rollout-batches` to regenerate. MONOLITH_ROLLOUT_BATCHES below
 * is hand-authored (monolith keys, not in the cookies list) and is preserved verbatim across
 * regenerations.
 *
 * Keys are stored columnar (per storage, per match kind) to keep the bundle small. Gate
 * names are string literals (Jira requires literal fg() arguments). Matching and
 * consumption of these batches lives in ./shouldEnforceKeyForRollout.tsx.
 */
import { fg } from '@atlassian/jira-feature-gating';

export type Gate = () => boolean;

/**
 * Every generated batch below (the SN canary + the team batch-N batches + the cross-product
 * all-N batches) is gated behind BOTH the shared "Targeting Gate"
 * `jfp-vulcan-browser-storage-rollout-targeting` AND its own per-batch gate:
 * `() => fg('…-targeting') && fg('…-batch-N')`. A single percentage change on the targeting gate
 * in Switcheroo therefore ramps the SAME cohort of users across every batch at once (each batch
 * shares the targeting gate's hashing/bucketing), while an individual batch gate can still be
 * paused/rolled back on its own without touching the others. Both `fg()` calls are inlined at
 * the callsite (never wrapped in a helper) so the gates stay statically analyzable. The
 * hand-authored MONOLITH_ROLLOUT_BATCHES further below is intentionally NOT behind the targeting
 * gate and ramps purely on its own gates.
 */
export type EntryKind = 'exact' | 'startsWith' | 'endsWith' | 'regexp';

export type KeyMatchers = Partial<Record<EntryKind, readonly string[]>>;

export type RolloutBatch = {
	isEnabled: Gate;
	entries: {
		local?: KeyMatchers;
		session?: KeyMatchers;
		indexeddb?: KeyMatchers;
	};
};

/**
 * Strictly-necessary keys — all in a single batch. These can never be blocked by
 * enforcement (the consent pipeline always allows StrictlyNecessary) and most are written
 * via the dedicated `setStrictlyNecessaryItem` API that bypasses this callback entirely —
 * so they act as a zero-risk canary at the front of the ramp and are not capped. Kept
 * separate so the whole group can be dropped later by removing it from the spread in
 * ./shouldEnforceKeyForRollout.tsx.
 */
export const SN_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// Strictly-necessary keys (all, 84)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-sn'),
		entries: {
			local: {
				exact: [
					'__storejs_agent-picker_coding-agents-discovery-dismissed',
					'__storejs_jira-board-stand-up-panel_timer-muted',
					'__storejs_jira-board-stand-up-panel_timer-value',
					'__storejs_jira-nav4-benefits-modal_jiraNav4BenefitsModal',
					'__storejs_jira-redaction-flag_lastViewedTimestamp',
					'__storejs_jira_plans_calendar-view_plans_calendar_view_settings',
					'__storejs_jsm-insight-reports-assets-deeplinking-modal_modalOpen',
					'__storejs_jsm-insight-reports-filters-onboarding-modal_modalOpen',
					'__storejs_servicedesk-live-chat-common_declined-chats',
					'__storejs_servicedesk-schedules_wfo-forecasting-banner-dismissal',
					'__storejs_usage-limits-by-product_usageLimitsByProductCache',
					'atlassian-analytics.jira',
					'atlassian-analytics.jira.lock',
					'gh.nonWorkingDaysShown.version',
					'lastReloadTimestamp',
					'liveChatIssueView:sidebar:width',
					'navigation_next_ui_state',
					'persist:@portfolio/common/sections2',
					'plansDependenciesReportZoomOnScroll',
					'polaris-pendo-strictly-necessary',
					'reloadAttempts',
					'this_site_organization_id',
					'atlassian-analytics.unknown.lock',
					'gh.presentation.mode',
					'gh.welcomeMode',
					'STATSIG_JS_LITE_LOCAL_OVERRIDES',
					'STATSIG_LOGS',
					'STATSIG_STABLE_ID',
					'__storage_test__',
					'aiMateSmartLinkProviderMapping',
					'awc.session.expiry',
					'feature-flags-atl.storage.support',
					'search-config',
				],
				startsWith: [
					'@atlassiansox/flight-deck-frontend-client_app-switcher-fe_prod_commercial',
					'__storejs___storejs_',
					'__storejs_issue-drafts_',
					'__storejs_polaris_description.draft.',
					'aiMateChatLoadPreference',
					'description.draft',
					'jsm.cs.customers-list.import.media.id_',
					'jsm.cs.organizations-list.import.media.id_',
					'ld:',
					'nav-platform-sidebar-state',
					'polaris.description.draft.',
					'rovo-navigation-filters-',
					'rovo-reintroduction-modal.dismissed',
					'showBlockedAppsBanner-',
					'statsig.session_id.',
					'timelineToken_',
					'connect.cookie:',
					'statsig.failed_logs.',
					'__storejs_jira-software-view-settings_view-settings-',
				],
				endsWith: [
					'568765c2-daf3-47fb-7b95-fe1569e5a0d9',
					'rovo.fomo.nudge.dismissed',
					'search-combined-config',
					'growth.experiment.confluence.cross-join-nudges-eligibility',
					'persisted.agent.access.warning.dismissed',
					'persisted.ecosystem.user.theme.preference',
					'persisted.imports.import-feedback-disabled',
					'persisted.legacy-content.report.task-id',
					'persisted.loom-auto-provision.spotlight.seen',
					'persisted.loom-cross-sell.discoverability-pulses.have-ended',
					'persisted.omnibar.pinned',
					'persisted.platform.quickfind.ghosttext',
					'persisted.recap.sidebar.collapsed',
					'persisted.should-use-gic-embed',
					'persisted.simplify-nav.new-user-spotlight.dismissed',
					'persisted.smartlink-auth-nudge.dont-show-again',
				],
				regexp: [
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_filter$',
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_hideDone$',
					'^__storejs_jwm-view-preferences_[^_]+_[^_]+_sorting$',
					'^__storejs_jwm-view-preferences_[^_]+_list_fieldsDetail$',
					'^__storejs_jwm-view-preferences_[^_]+_list_groupBy$',
					'^persist:calendar\\/plan\\/[^/]+\\/scenario\\/[^/]+$',
				],
			},
			session: {
				exact: [
					'__storejs_onboarding-core_projectCreateJwmOnboarding',
					'__storejs_servicedesk-live-chat-common_notification-permission-banner-dismissed',
					'__storejs_sidebar-navigation_back-button-url',
					'awc.taskSessionsInit',
					'classicBatchUrl',
					'media-api-environment',
					'media-api-region',
				],
				startsWith: ['__storejs___storejs_', '__storejs_have-i-seen-it-', 'addProject:Sel'],
			},
		},
	},
];

/**
 * Non-strictly-necessary keys, grouped by team into at most 11 batches (a hard cap on the
 * batch count). Unlike the SN batch these carry real blocking risk once enforced (denied
 * consent blocks the write), so they ramp after the SN canary. A --from-scratch repack
 * keeps teams intact within the 11 batches; incremental runs append new keys to the batch
 * already hosting their team — or, once 11 batches exist, to the smallest batch (which may
 * grow past the from-scratch size) — and never move existing keys between batches.
 */
export const ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// Teams: Astral, Gordian, Cross flow (Growth) (30 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-1'),
		entries: {
			local: {
				exact: [
					'__storejs_assets-schema-tree-sidebar_sidebar-width',
					'__storejs_jira-assets-sidebar_sideBarWidth',
					'__storejs_jira-servicedesk-insight-object-details_currentObjectId',
					'__storejs_jira-servicedesk-insight-object-details_currentTab',
					'__storejs_jsm-assets-object-usage-info_dismissed',
					'__storejs_jsm-cbp-common-info_cbp-change-notifications-banner-dismissed',
					'__storejs_jsm-cbp-common-info_cbp-change-notifications-flag-dismissed',
					'__storejs_jsm-cbp-pre-release_dismissed',
					'__storejs_jsm-cmdb-linked-issues-panel_hideResolvedIssues',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilter',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilterCloudId',
					'__storejs_jsm-cmdb-linked-issues-panel_selectedFilterName',
					'__storejs_jsm-cmdb-linked-issues-panel_textFieldValue',
					'__storejs_jsm-cmdb-object-graph_objectGraphConfiguration',
					'__storejs_jsm-cmdb-schema_viewMode',
					'__storejs_schema-graph-config_toShowLinksToParentObjects',
					'__storejs_schema-graph-config_toShowObjectCount',
					'edit-issue-layout-permission-discovery-message',
					'edit-workflow-permission-discovery-message',
					'issuelayout-landing-edit-issue-layout-permission-discovery-message',
					'manage-versions-permission-discovery-message',
				],
				startsWith: [
					'__storejs_cmdb-object-hide-empty-attributes_hideEmptyAttributes_',
					'__storejs_jira_loom_ad_controls_',
					'__storejs_jsm-cmdb-insight-object-search_localLastSearchQuery-',
					'__storejs_jsm-cmdb-insight-object-search_object-type:',
					'__storejs_jsm-cmdb-insight-object-search_searchMode-',
					'__storejs_jsm-cmdb-object-graph_objectType',
					'__storejs_jsm-cmdb-object-timeline_insight_drafts.object_comment.',
					'__storejs_jsm-cmdb-quick-search_quickSearchSelectedAttrs_schema',
					'__storejs_uniapa_has-spotlight-been-shown_',
				],
			},
		},
	},
	// Teams: A4T-Tanuki, Editor Jenga, Automation Platform / Automation, JFP Magma (27 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-2'),
		entries: {
			local: {
				exact: [
					'__storejs_backlog_gh.detailViewWidth',
					'__storejs_backlog_gh.workloadModal.subtasks',
					'__storejs_backlog_panel-state',
					'__storejs_backlog_selectedIssueType',
					'__storejs_number_currency_formatting_changeboarding_flag_local_storage_should_show_number_currency_formatting_changeboarding',
					'__storejs_software_board-detail-view-mode',
					'__storejs_software_collapsed-swimlanes',
					'__storejs_software_gh.detailViewWidth',
					'__storejs_whiteboardsTouchpoint_whiteboardsExperimentHasClickedOnPulse',
					'__storejs_whiteboardsTouchpoint_whiteboards_backlog_jira_confluence_data',
					'__storejs_jira-synced-block-feedback_synced-block-destructive-feedback-prompt-shown',
				],
				startsWith: [
					'__storejs_backlog_gh.backlogCardGroupSettings.',
					'__storejs_backlog_gh.backlogSettings.',
					'__storejs_backlog_panel-state-',
					'__storejs_software_backlog-filters.',
					'__storejs_software_board-filters.',
					'__storejs_software_gh.groupBySelection.',
					'automation-frontend-ff-overrides-',
					'automation-rule-id-',
					'gh.epicShownOnRapidBoard.',
					'gh.statusShownOnRapidBoard.',
					'gh.versionShownOnRapidBoard.',
				],
			},
			session: {
				exact: [
					'__storejs_navigation_returnToProjectUrl',
					'__storejs_whiteboards-entrypoint_whiteboard_backlog_jira_ui_button_clicked_confirmbutton_xflow',
					'__storejs_whiteboards-entrypoint_whiteboard_backlog_jira_ui_user_properties',
				],
				startsWith: ['__storejs_quickfilters_', 'jira.messages'],
			},
		},
	},
	// Teams: Issue View - Pulsar, Issue View - Tenet, Olympus (27 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-3'),
		entries: {
			local: {
				exact: [
					'__storejs_atl-issue-view-comment_unread-comment-lozenge-viewed',
					'__storejs_cache_prefix___scope_key__',
					'__storejs_cache_prefix_board-etag',
					'__storejs_cache_prefix_components',
					'__storejs_cache_prefix_epics',
					'__storejs_cache_prefix_labels',
					'__storejs_cache_prefix_roadmap-sprint-details-cache',
					'__storejs_cache_prefix_roadmap-version-details-cache',
					'__storejs_cache_prefix_sprints',
					'__storejs_cache_prefix_versions',
					'__storejs_jira-issue-view-comment-placeholder_hideAddCommentPlaceholder',
					'__storejs_placeholderTemplateAddProductPermissionStorage_confluence-add-product-permission',
					'edit_sprint_cache_value',
				],
				startsWith: [
					'__storejs_cache_prefix_',
					'__storejs_confluenceContentPlaceholderTemplate_hasActivatedConfluenceWithinDays_',
					'__storejs_issue-mobile-section-message_',
					'__storejs_placeholderTemplateStorage_hasDismissedConfluenceDummyLinkCoUse_',
					'__storejs_placeholderTemplateStorage_hasDismissedConfluenceDummyLink_',
					'__storejs_placeholderTemplateStorage_hasDismissedlistViewEntrypoint_',
					'__storejs_placeholderTemplateStorage_hasDismissedwhiteboardsEntrypoint_',
				],
				endsWith: [
					'.issue-panel-pin-to-project.clicked',
					'_CHILD_WORK_ITEM_TABLE_COLUMN_SORT_CONFIG',
				],
			},
			session: {
				exact: [
					'__storejs_cache_prefix_roadmap-sprint-details-cache',
					'__storejs_cache_prefix_roadmap-version-details-cache',
					'__storejs_confluence-permitted-session-storage_user-confluence-permitted',
				],
				startsWith: ['__storejs_cache_prefix_', 'applink'],
			},
		},
	},
	// Teams: Growth: Puma, CC Flywheel Activation, JSD SHIELD, AI Mate (28 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-4'),
		entries: {
			local: {
				exact: [
					'__storejs_cache_prefix_cjn-eligibility-cache',
					'__storejs_cross-join-nudge-conf-capabilities-api-cache_cross.join.confluence-capabilities.cache.key',
					'__storejs_cross-join-nudge-conf-perms-api-cache_cross.join.confluence.cache.key',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.confluence.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.jpd.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.loom.visited',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.msr',
					'__storejs_cross-join-nudge-visited_cross.join.nudges.your.work',
					'__storejs_grw-1287-pcjn_access-request-capabilities',
					'__storejs_grw-1287-pcjn_confluence-collaborators-userid',
					'__storejs_grw-1287-pcjn_has-dismissed-feature',
					'__storejs_nudge-bundle-users_flag-dismissed',
					'__storejs_nudge-inactive-confluence-user-access-api-cache_nudge-inactive-confluence-user.confluence-access.cache.key',
					'__storejs_nudge-inactive-confluence-user-capabilities-api-cache_nudge-inactive-confluence-user.confluence-capabilities.cache.key',
					'__storejs_nudge-inactive-confluence-user_initial-dismissed-date',
					'__storejs_servicedesk_queues_queue-groups-nav-state',
					'__storejs_servicedesk_queues_queues-category-state',
					'invitedAppShortcuts.eligibilityChecked',
					'invitedAppShortcuts.featureExposed',
					'queueIssueView:sidebar:width',
					'ai-mate-auto-apply-enabled',
					'rovo-connectors-metadata',
				],
				startsWith: [
					'__storejs_jsm-all-queues-state_is-open-',
					'__storejs_jsm-queue-groups-state_selected-group-id-',
					'__storejs_servicedesk_queues_issues_',
					'__storejs_servicedesk_queues_last_visited_category_',
					'__storejs_servicedesk_queues_last_visited_queue_',
					'persist:calendar/view/ari:cloud:jira:',
				],
			},
		},
	},
	// Teams: Confluence Integrations, Product Integrations, Endeavour, jira-werewolves, Confluence Growth (27 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-5'),
		entries: {
			local: {
				exact: [
					'__storejs_create-retro-checkbox_isChecked',
					'__storejs_create-retro-type-selector_selectedRetroType',
					'__storejs_cross-join-third-party-nudges-v2-visited_cross.join.third.party.nudges.v2.jira.visited',
					'__storejs_dashboard_AG.congolmerate.cookie',
					'__storejs_dashboard_dashboard-data',
					'__storejs_form-templates_formTemplateSelectionToggleKey',
					'__storejs_jira-insights-should-use-jql_shouldUseJQLQuery',
					'__storejs_jira-software-code-tab_clickedGoToDevelopment',
					'__storejs_jira-software-security-tab_clickedGoToDevelopment',
					'__storejs_jira-summary-reports-banner_reports-customization-dismissed',
					'__storejs_jira_integration_spotlight_throttle_jira_3p_within_1p_spotlight_last_shown_timestamp',
					'__storejs_jira_integration_spotlight_throttle_jira_integration_spotlight_last_shown_timestamp',
					'__storejs_team-field-onboarding_TEAM_FIELD_ONBOARDING_BANNER_DISMISSED',
					'__storejs_team-field-onboarding_clicked',
					'__storejs_third-party-nudge_has-seen-nudge',
					'__storejs_use-is-space-auto-created_twcg-390-space-auto-created',
				],
				startsWith: [
					'__storejs_auto-expand-halfway-alert-',
					'__storejs_jira-list-view-statistics-',
					'__storejs_jira_integration_spotlight_trait_',
					'rovo-ghost-icons-',
				],
			},
			session: {
				exact: [
					'__storejs_confluence-integration-controls-session-storage_confluence-integration-controls',
					'__storejs_confluence-permissions-session-storage_user-confluence-access',
					'__storejs_create-confluence-content_userConfluenceAccessStatus',
					'__storejs_third-party-nudge_issues-view-nudge-date',
					'__storejs_third-party-nudge_issues-watch-nudge-state',
					'__storejs_third-party-nudge_notification-section-nudge-state',
					'productfruits_has_possible_csp_issues',
				],
			},
		},
	},
	// Teams: Alexstrasza, A4T-Pandora, MorphineCake, Galileo (Growth), Keystone, Media Exif (27 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-6'),
		entries: {
			local: {
				exact: [
					'__storejs_alert_filters_popout_jira-ops-alert-filters-last-visited-search',
					'__storejs_github-for-jira-arc_github-for-jira.configuration.message.closed',
					'__storejs_issue-layout-editor_project-settings-product-features-jsw-cmp.sidebar.get-permissions-info-panel-dismiss-button',
					'__storejs_jira-jpd-roadmaps-upsell_jpd-roadmaps-upsell.dismissed',
					'__storejs_jira-onboarding-hub-cta-panel_dismissed',
					'__storejs_jsm-change-calendar_assigneeFilterItems',
					'__storejs_jsm-change-calendar_filterStatus',
					'__storejs_jsm-change-calendar_moreFilterItems',
					'__storejs_jsm-change-calendar_project',
					'__storejs_nfa-onboarding_cachedFieldConfigSchemes__v1',
					'__storejs_nfa-onboarding_cachedFields__v2',
					'__storejs_nfa-onboarding_cachedProjects__v1',
					'__storejs_nfa-onboarding_rejectedFieldIds',
					'__storejs_onboarding_teamType',
					'__storejs_sprint-summary-checkbox_isChecked',
					'__storejs_work-suggestions-github-onboarding-finished_finished',
					'calendar:settings:business',
					'calendar:settings:software',
					'calendar:sidebar:width',
					'jsm-ops-xflow-onboarding',
					'selectedConfigTab',
				],
				startsWith: [
					'__storejs_jira-project-create_project-create.show-auto-provisioned-onboarding.',
					'calendar:settings:software:board:',
					'time-saver-default-time-',
				],
				regexp: ['^__storejs_jira_business_.+-view_.+_hierarchy_levels$'],
			},
			session: {
				exact: ['__storejs_templates-sidebar-focus_trigger-element-test-id'],
				startsWith: ['__storejs_avatar-modal_AVATARS_ISSUE_TYPE_'],
			},
		},
	},
	// Team: Jlove-Makkuro (28 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-7'),
		entries: {
			local: {
				exact: [
					'__storejs_jira-onboarding-personalisation_response',
					'__storejs_jiraSoftwareOnboardingV2_onboarding-selected-project-template-key',
					'__storejs_jsw-onboarding-premium-reverse-trial_premiumTrialOnSignup',
					'__storejs_jsw-onboarding-premium-reverse-trial_reverseTrialCohort',
					'__storejs_onboarding-core_onboarding_started_timestamp',
					'__storejs_onboarding-core_team-type',
					'__storejs_onboarding-core_team-type-shorthand',
					'quickstart.jsm-business-checklist.visibility',
					'quickstart.jsw-onboarding-checklist.visibility',
				],
				startsWith: [
					'__storejs_jira-initial-project-template-name_',
					'__storejs_jira_business_list-view_',
					'__storejs_list-view-simplification_ENABLED_FEATURES_',
					'__storejs_quickstart_',
				],
				regexp: [
					'^__storejs_jwm-view-preferences_(?![^_]+_list_groupBy$)(?![^_]+_list_fieldsDetail$)(?![^_]+_[^_]+_filter$)(?![^_]+_[^_]+_hideDone$)(?![^_]+_[^_]+_sorting$).+$',
				],
			},
			session: {
				exact: [
					'__storejs_jira-user-cross-project-board-trait_jira-user-cross-project-board-trait',
					'__storejs_jsw-onboarding-checklist_accountId.jsw-onboarding-checklist.has.navigated.from.link',
					'__storejs_onboarding-core_can-enroll-in-config-wizard-view',
					'__storejs_onboarding-core_project_updated_in_welcome_modal',
					'__storejs_onboarding-core_should-hide-quickstart-for-personalized-sample-data',
					'__storejs_onboarding-core_should-show-list-view-fields-dropdown-spotlight',
					'__storejs_onboarding-core_should-show-list-view-type-cell-spotlight',
					'__storejs_onboarding-core_should-show-personlized-sample-data-for-kanban',
					'__storejs_onboarding-core_should-show-welcome-modal',
					'__storejs_onboarding-core_showed-welcome-modal-during-signup',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectListExpandProperty',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectNavNudgeProperty',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectNudgesProperty',
					'__storejs_sampleProjectSessionStoreKey_matureExampleProjectV2NudgesProperty',
				],
			},
		},
	},
	// Teams: jdm-streamlining-tools, Self-Help Experiences, Jolt, Reverie, Issue View - Thor, JPD - Juno, Issue View - Kenji, Polaris, Work Item View - Vanguard, Navigation, Lodestone, Luna (Growth), JSM Sherpas, TRex - Rovo Button (27 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-8'),
		entries: {
			local: {
				exact: [
					'__storejs_admin-pages-performance_cfo_experience_preference',
					'__storejs_admin-pages-performance_cfo_new_exp_banner_dismissed',
					'__storejs_assets-app-for-you-page_showEssentialResources',
					'__storejs_code_in_jira-code-page_dismissedEmptyRepoCards',
					'__storejs_development-summary-open-in-ide_last-open-target',
					'__storejs_development-summary-open-in-ide_recent-cli-paths',
					'__storejs_hasPinFieldInfoBannerShown_hasPinFieldInfoBannerShown',
					'__storejs_incidents_show_more_discovery_shouldShowDiscovery',
					'__storejs_issue-ecosystem_collapsed-panels',
					'__storejs_jira-roadmap-on-timeline_jpd-roadmap-on-timeline.button-dismissed',
					'__storejs_jira-roadmap-on-timeline_jpd-roadmap-on-timeline.spotlight-shown',
					'__storejs_jira_issue_view_view-mode',
					'__storejs_polaris_last-used-date-field-scale',
					'__storejs_polaris_sidebar-width',
					'__storejs_project-scoped-labels_restrict_labels_to_projects_checkbox_value',
					'__storejs_projects-menu-jpd-recommendation_app-recommendation-state',
				],
				startsWith: [
					'__storejs_compass-components_spotlight-viewed-',
					'__storejs_compass-components_table-crossflow-dismissed-',
					'__storejs_jira-auto-upgrade-not-a-trial_',
				],
				endsWith: [
					'upsell_jira_global_automations_limit_banner_upsell_jira_global_automations_limit_banner',
				],
			},
			session: {
				exact: [
					'__storejs_atl-issue-view_first-viewed-issue',
					'__storejs_hr-space_dualSpaceEligibilityStates',
					'__storejs_hr-space_labelBlankSpaceEligibilityStates',
					'__storejs_iph_history',
					'__storejs_page-container_navigation-journey',
					'rovo-button-unread-status-v1',
				],
				startsWith: ['__storejs_jira-platform-persistent-cache_user-property-'],
			},
		},
	},
	// Teams: X2C Wolverine, Jira - Yurree, ITSM Agni, JSM Phoenix, Empanada, Jira - Warepil, Helix, Jira Chronos (26 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-9'),
		entries: {
			local: {
				exact: [
					'X2C-Migration-Id-Store',
					'__storejs_jira-board-issue-create-provider_board__preferred__issue__type__id',
					'__storejs_jira-board-stand-up-panel_skipped-users',
					'__storejs_jira-harmonized-timeline_timeline:sidebar:width',
					'__storejs_jira_business_common_jwm.sidebar',
					'__storejs_jsm-linked-alerts_saved-search-id',
					'__storejs_jsm-linked-alerts_text-search',
					'__storejs_jsm-surveys-dashboard_dashboardViewMode',
					'__storejs_jwm-view-preferences_timeline_project-mapping-preference',
					'__storejs_nin-changeboarding_jira.nin.global.scope.enrolment',
					'__storejs_nin-changeboarding_jira.user.nin.global.scope.enrolment.flag.disabled',
					'__storejs_servicedesk-linked-object-incidents-store_cross-tab-cache',
					'advanced-roadmaps-request-premium-trial',
				],
				startsWith: [
					'__storejs_dead-end-access-request_',
					'__storejs_jira-native-issue-table-inline-issue-create_',
					'__storejs_jira-standups_participants-board-',
					'__storejs_jira-standups_settings-board-',
					'workflow-an-message-dismissed#',
				],
				endsWith: ['advanced-roadmaps-request-premium-trial', 'last-used-date-field-scale'],
			},
			session: {
				exact: [
					'SHOW_NEW_EDITOR_SUCCESS_FLAG',
					'SHOW_OLD_EDITOR_SUCCESS_FLAG',
					'__storejs_sla-breach-configuration-banner-session_sla-banner-navigating-from-reports',
					'__storejs_sla-breach-configuration-banner_sla-configuration-banner-dismissed',
					'showGinOptOutFlag',
				],
				startsWith: ['__storejs_plan-invite-permissions_'],
			},
		},
	},
	// Teams: AI Mate, __uncategorized__, Atlassian Analytics Pipeline, Dropbears (44 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-10'),
		entries: {
			local: {
				exact: [
					'aiMateSmartLinkProviders',
					'cjn-eligibility-cache',
					'cjn-event-eligibility-cache',
					'cross.join.nudges.msr',
					'cross.join.nudges.msr.count',
					'jira.create-with-rovo-modal-close-feedback-flag.last-shown',
					'jsm-cmdb-schema.viewMode',
					'polaris-last-used-date-field-scale',
					'polaris-pendo-functional',
					'polaris-route-slug',
					'polaris-sidebar-width',
					'polaris_sidebar_width',
					'DEBUG_rovo_chat_fullscreen',
					'EXP_unsent_$default_instance',
					'_uetvid',
					'_uetvid_exp',
					'aiMateChatLoadPreference',
					'aiMateUserPreferences',
					'algoliasearch-client-js',
					'awc.ui.viewed.last.sent',
					'jam_ephemeral_events_host-network-events',
					'rovoChatLastMessageSentAt',
					'rovo-service-agent.widget.is-open',
				],
				startsWith: [
					'cmdb-object-hide-empty-attributes',
					'jsm-change-calendar',
					'jsm-cmdb-insight-object-search',
					'jsm-cmdb-linked-issues-panel',
					'jsm-cmdb-object-graph',
					'jsm-cmdb-object-timeline.insight_drafts.object_comment',
					'jsm-cmdb-quick-search',
					'jsm-linked-alerts',
					'polaris-description-draft-',
					'schema-graph-config',
					'servicedesk-linked-object-incidents-store',
					'_pendo_accountId',
					'_pendo_guides_blocked',
					'_pendo_meta',
					'_pendo_visitorId',
				],
				endsWith: ['-ends-with-test', '__csm-requests-list__layout'],
			},
			session: {
				exact: ['awc.last.screen.event', 'awc.tab.id', 'shortstackIssueMetadata'],
				startsWith: ['@atlassian/switcher-team-25-experiment-cohort'],
			},
		},
	},
	// Teams: JSM Request Intake, Deliveroo, Boysenberry, Wanjel, Yamato, Groot, Crocs, Smite, JPD - Pulsar, Gravity-AI, Studio Expansion, Atlassian Analytics Pipeline, Jira Cosmos, JSM nebula, Advanced Roadmaps, Navigation Experiences - Linking Platform, JSM ASAP AI, Eng - Ecosystem - Enterprise, Starburst, Aurum, Gryffindor, Jira Notifications Platform, Jlove-Makkuro, JFP - Nike, Studio Expansion - Growth (28 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-batch-11'),
		entries: {
			local: {
				exact: [
					'LastSelectedFormDetails',
					'__storejs_bulk_operations_sidebar_ratio_sidebar_ratio',
					'__storejs_issue-layout-editor_project-settings-product-features-jcs.sidebar.get-permissions-info-panel-dismiss-button',
					'__storejs_jira-forge-compal_forge-compal-modules-cache',
					'__storejs_jiraAdminPagesFields_initialState',
					'__storejs_jira_3p_within_1p_spotlight_last_shown_timestamp',
					'__storejs_jira_ai_agent_orchestration_analytics_jira_ai_agent_orchestration_analytics',
					'__storejs_jsm-va-limit-info_dismissed',
					'__storejs_polaris_routeslug',
					'__storejs_related-resources-site-switcher_related-resources-site-switcher',
					'studio-entry-point-banner-dismissed',
					'awc.malformed.uiviewed.last.sent',
				],
				startsWith: [
					'__storejs_navigation-apps-sidebar-nav4-project-settings_',
					'__storejs_suggested-topic-feedback_',
					'persist:jpo-app-simple-plans-',
					'smart-card-social-proof_pct-map:v1:',
					'smart-card-social-proof_site-cloud-id:v1:',
					'__storejs_ai-summary-section_lastActivityTime:',
				],
				endsWith: ['connect-eos-banner_dismissed'],
			},
			session: {
				exact: [
					'JIRA_LINKED_CONFLUENCE_DOC_ROVO_NUDGE_DISMISSED',
					'__storejs_atlassian-jira-dm-notifications_exportedObjectsListFileStatus',
					'__storejs_jira-issue-create-restore-_issue-restore-meta',
					'__storejs_personal-settings_show-success-flag',
					'__storejs_sampleProjectSessionStoreKey_sampleProjectTimelineExpandProperty',
					'awc.taskSessions',
					'clicks-before-tti',
					'awc.interactionSession.source',
				],
				startsWith: ['automation.rovo-suggestion-banner.'],
			},
		},
	},
];

/**
 * Cross-product (`product = all`) keys — kept separate from the Jira SN/team batches so
 * they ramp on their own gates. Capped by COUNT at 2 batches (not by size): a --from-scratch
 * repack keeps teams intact within the 2 `…rollout-all-N` batches; incremental runs append
 * new keys to the batch already hosting their team — or, once 2 batches exist, to the
 * smaller batch — and never move existing keys between batches.
 */
export const CROSS_PRODUCT_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// Teams: Knowledge Discovery, __uncategorized__, Search Platform: Search Experience, Enterprise Readiness: Consent Pals, Navigation Experiences - Linking Platform (61 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-all-1'),
		entries: {
			local: {
				exact: [
					'@atlassian.search-ai.add-new-pin-card-dismissed',
					'@atlassian.search-ai.jira-issue-url',
					'AS_ORG_SELECTOR_WIDTH',
					'CHART_EDITOR.SELECTED_DATASOURCE_BY_ORG',
					'__storejs_platform_educational_discount_dismissed',
					'accounts-by-aaid-cache',
					'assets-workspace-id',
					'atlassian.search-page.visited',
					'atlassian.unified-search.github-tip-dismiss',
					'atlassian.unified-search.oauth-banner-snooze',
					'atlassian.unified-search.quick-find-rovo-tip',
					'atlassian.unified-search.recent-searches',
					'automation.execution.limit',
					'avp.activity_stream_banner_feedback.dismissed',
					'avp.dashboard_dogfooding.dismissed',
					'avp.preferred_view',
					'avp.test_full',
					'cachedImageErrorDark',
					'cachedImageErrorLight',
					'chartio.collapsed_dashboard_nav',
					'csrfToken',
					'heartbeat-last-session-expiry',
					'jiraNav4BenefitsModal_dismissed',
					'lastActivePageId',
					'loom-user-auth',
					'mediaViewer.hasLocalStorage',
					'outbound-auth-cache',
					'quick-find-ghost-text',
					'quick-find-recent-activities',
					'quick-find-recommended-documents',
					'quick-find-user-documents',
					'resultTableHeightPercent',
					'search-ai.sain.store_message',
					'search-page-hidden-filters-v2',
					'search-page-rovo-browser-extension-banner-state',
					'search-page.auto-open-chat-dismiss-count',
					'search-page.sain-on-demand-banner-dismiss',
					'search-personalization-site-user',
					'search-personalization-smartlink-site',
					'search-personalization-smartlink-usage-and-top-apps-site',
					'session-banner-dismissed',
					'shania-20',
					'shania-test-storage-item',
					'storageTest',
					'search-3p-config',
				],
				endsWith: ['_third-party-action-nudge-dismissed:v1'],
			},
			session: {
				exact: [
					'@atlassian/switcher-overflow-experiment-cohort',
					'@atlassiansox/flight-deck-frontend-client_app-switcher-fe_prod_commercial',
					'AUTOMATION_TEST_STACK_ID',
					'STATSIG_OVERRIDES',
					'__storage_test__',
					'atlassian.search-ai.ai-definition-toggle',
					'atlassian.search-dialog-filters',
					'atlassian.search-dialog-query',
					'atlassian.search-dialog-sticky',
					'atlassian.search-dialog.ai-answer-component',
					'atlassian.search-dialog.ai-dialog',
					'atlassian.search-dialog.ai-dialog.referrer',
					'atlassian.search-dialog.ai-topics-component',
					'convo_ai_sain_prompt_override_hello',
					'shania-test-storage-item',
				],
			},
		},
	},
	// Teams: Editor, Dropbears, Local Devloop, Identity - Zero, JFP Magma, Automation Platform / Automation, Atlassian Analytics, ASIMO, Atlassian Analytics Pipeline, Editor Ease of Use, Editor: Collaboration, Post Office, Rovo, TRex, Automation, TRex - Rovo Button, Editor: Lego, CSMAI, Studio Expansion - Growth, Editor FE Platform (144 keys)
	{
		isEnabled: () =>
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('jfp-vulcan-browser-storage-rollout-targeting') &&
			fg('jfp-vulcan-browser-storage-rollout-all-2'),
		entries: {
			local: {
				exact: [
					'@atlaskit/editor-plugin-card_smart-link-upgrade-pulse',
					'@atlassian/conversation-assistant-widget-installer__discovery',
					'STATSIG_OVERRIDES',
					'__TWILIO_CLIENT_TOKEN__',
					'__cuid',
					'__storejs__',
					'ai-rule-generation-has-been-generated-ever',
					'ajs_anonymous_id',
					'atlassian.unified-search.oauth-banner-dismiss-counts',
					'atlassian.unified-search.third-party-query-suggestions-dismissed',
					'automation-frontend-ff-favourites',
					'automation.onboarding.override',
					'awc.storage.support',
					'editor.tinyMCE.just.published',
					'fabric.emoji.lastUsed',
					'feedback-survey-email',
					'insert-wiki-markup-dialog.parser',
					'ncs-session-step-metrics-storage_ncsActiveSessions',
					'ncs-session-step-metrics-storage_ncsSessionStepMetrics',
					'notification-list.readStateFilter',
					'notifyWatchers',
					'quick_find_connect_apps_cooldown',
					'rovo-onboarding-checklist-item-states',
					'rovo.catch-up-linked-items.nudge.dismissed',
					'rovoChatCsatEligibility',
					'rovoOnboardingHoldout',
					'track-element-analytics.element-insertion-stats',
					'awc-dev.malformed.uiviewed.last.sent',
					'awc-dev.session.id',
					'awc-dev.storage.support',
					'awc-dev.ui.viewed.last.sent',
					'awc-local.malformed.uiviewed.last.sent',
					'awc-local.session.id',
					'awc-local.storage.support',
					'awc-local.ui.viewed.last.sent',
					'awc-staging.malformed.uiviewed.last.sent',
					'awc-staging.session.id',
					'awc-staging.storage.support',
					'awc-staging.ui.viewed.last.sent',
					'awc-test.malformed.uiviewed.last.sent',
					'awc-test.session.id',
					'awc-test.storage.support',
					'awc-test.ui.viewed.last.sent',
					'awc.session.id',
					'atl_xid.current',
					'atl_xid.ts',
					'atl_xid.xc',
					'__storejs__test__',
				],
				startsWith: ['ai-quick-prompt-standdown-', 'loglevel:'],
				endsWith: [
					'copied',
					'editor-controls.initial-preferences',
					'editor.survey.last.submitted',
					'editor:offline:last-background-cleanup-attempt',
					'markdown.view.mode',
					'onboarding.state.editor.custom.groups',
					'onboarding.state.editor.pinned.groups',
					'opiedCols',
					'persisted.editor-controls.onboarding-banner-new-pinning.dismissed',
					'persisted.editor-controls.onboarding-banner-updated.dismissed',
					'persisted.editor-controls.onboarding-banner.classic-page.dismissed',
					'persisted.editor-controls.onboarding-banner.dismissed',
					'persisted.editor-controls.onboarding-banner.live-page.dismissed',
					'state.editor.custom.groups.toggle',
				],
				regexp: [
					'^awc-dev\\.[^.]+\\.ack$',
					'^awc-dev\\.[^.]+\\.inProgress$',
					'^awc-dev\\.[^.]+\\.queue$',
					'^awc-dev\\.[^.]+\\.reclaimEnd$',
					'^awc-dev\\.[^.]+\\.reclaimStart$',
					'^awc-local\\.[^.]+\\.ack$',
					'^awc-local\\.[^.]+\\.inProgress$',
					'^awc-local\\.[^.]+\\.queue$',
					'^awc-local\\.[^.]+\\.reclaimEnd$',
					'^awc-local\\.[^.]+\\.reclaimStart$',
					'^awc-staging\\.[^.]+\\.ack$',
					'^awc-staging\\.[^.]+\\.inProgress$',
					'^awc-staging\\.[^.]+\\.queue$',
					'^awc-staging\\.[^.]+\\.reclaimEnd$',
					'^awc-staging\\.[^.]+\\.reclaimStart$',
					'^awc-test\\.[^.]+\\.ack$',
					'^awc-test\\.[^.]+\\.inProgress$',
					'^awc-test\\.[^.]+\\.queue$',
					'^awc-test\\.[^.]+\\.reclaimEnd$',
					'^awc-test\\.[^.]+\\.reclaimStart$',
					'^awc\\.[^.]+\\.ack$',
					'^awc\\.[^.]+\\.inProgress$',
					'^awc\\.[^.]+\\.queue$',
					'^awc\\.[^.]+\\.reclaimEnd$',
					'^awc\\.[^.]+\\.reclaimStart$',
				],
			},
			session: {
				exact: [
					'AP-RULE-BUILDER-FEEDBACK-SEEN',
					'awc.interactionSession.bridge',
					'awc.interactionSession.expiry',
					'awc.interactionSession.id',
					'awc.interactionSession.id.prev',
					'awc.storage.support',
					'rovo-button-nudges-ots',
					'rovo-button-nudges-suppression',
					'smart-card-url-data_response-cache',
					'awc-dev.interactionSession.expiry',
					'awc-dev.interactionSession.id',
					'awc-dev.interactionSession.id.prev',
					'awc-dev.last.screen.event',
					'awc-dev.session.expiry',
					'awc-dev.storage.support',
					'awc-dev.tab.id',
					'awc-dev.taskSessions',
					'awc-dev.taskSessionsInit',
					'awc-local.interactionSession.expiry',
					'awc-local.interactionSession.id',
					'awc-local.interactionSession.id.prev',
					'awc-local.last.screen.event',
					'awc-local.session.expiry',
					'awc-local.storage.support',
					'awc-local.tab.id',
					'awc-local.taskSessions',
					'awc-local.taskSessionsInit',
					'awc-staging.interactionSession.expiry',
					'awc-staging.interactionSession.id',
					'awc-staging.interactionSession.id.prev',
					'awc-staging.last.screen.event',
					'awc-staging.session.expiry',
					'awc-staging.storage.support',
					'awc-staging.tab.id',
					'awc-staging.taskSessions',
					'awc-staging.taskSessionsInit',
					'awc-test.interactionSession.expiry',
					'awc-test.interactionSession.id',
					'awc-test.interactionSession.id.prev',
					'awc-test.last.screen.event',
					'awc-test.session.expiry',
					'awc-test.storage.support',
					'awc-test.tab.id',
					'awc-test.taskSessions',
					'awc-test.taskSessionsInit',
					'awc.session.expiry',
					'__storejs__',
					'__storejs__test__',
				],
				startsWith: [
					'csm:consent-form:accepted:',
					'editor-persit-scroll-position',
					'automation.changeboarding.modal',
				],
				endsWith: ['smart-card-url-data'],
				regexp: ['^.+::document::.+$', '^.+::list::.+$', '^.+::map::.+$'],
			},
		},
	},
];

/**
 * Known browser-extension keys — these patterns identify storage keys written by
 * third-party browser extensions (e.g. workflow helpers, test harnesses, debugging tools)
 * rather than by Jira or its cross-product dependencies. They are excluded from the
 * unbatched catch-all gate so that extension-owned keys are never inadvertently blocked
 * by enforcement that is meant for Jira-owned keys only. A key that matches one of these
 * patterns returns `false` from `shouldEnforceKeyForRollout` regardless of any catch-all
 * or per-batch gate.
 *
 * IndexedDB: exact database names written by known extensions.
 * localStorage / sessionStorage: regexp patterns covering the key prefixes / shapes used
 * by those extensions.
 */
export const KNOWN_BROWSER_EXTENSION_KEYS: {
	indexeddb: readonly string[];
	localOrSession: readonly RegExp[];
} = {
	// IndexedDB database names created by browser extensions
	indexeddb: ['CannedResponses', 'indexedDbCheck', 'ticket_db', 'WFX_EventsDB', 'DeepUiStorage'],
	// localStorage / sessionStorage key patterns from browser extensions.
	// `wm-*` covers any key starting with "wm-".
	localOrSession: [
		/^wm-.*/,
		/^jiraBarData\..*/,
		/^rx\..*/,
		/^shortstack\..*/,
		/^_?wfx\..*/,
		/^hopscotch\..*/,
	],
};

/**
 * Jira monolith (server-rendered legacy) web-storage keys, sourced from the
 * "Jira Monolith Session and Local storage usages" audit. These keys are owned by the
 * monolith rather than AFM and are grouped here into dedicated, independently rampable
 * gates so that monolith enforcement can be rolled out (and rolled back) separately from
 * the AFM team batches above. CLASSIFIED keys are intentionally omitted (already handled
 * by AFM classification); high-traffic or single-purpose keys get their own gate, and the
 * remaining low-traffic keys are grouped by their analytics status (READ ONLY / ACTIVE)
 * with a final catch-all for anything left over.
 */
export const MONOLITH_ROLLOUT_BATCHES: readonly RolloutBatch[] = [
	// All monolith web-storage and IndexedDB keys consolidated into a single always-on
	// batch. Enforcement for every monolith-owned key ramps together and is fully enabled
	// (`isEnabled: () => true`).
	{
		isEnabled: () => true,
		entries: {
			local: {
				exact: [
					'gh.charts.intro',
					'gh.detailViewWork',
					'gh.latestRapidViewId',
					'gh.mode',
					'gh.nonWorkingDaysShown.burndown',
					'media-picker.herment.storage',
					'twixi',
					'__storejs_navigation-session-provider_navigationSessionId',
					'block-states',
					'jira.wikieditor.last.color',
					'kanplan.acknowledge.display',
					'lhc-state',
					'navSimpleSearch',
					'parentpicker.showdone',
					'ui.sidebar.layoutview-draggable',
					'workflow-landing-edit-workflow-permission-discovery-message',
				],
				startsWith: [
					'gh.boardSettings.',
					'AJS.Shortener#',
					'jira.projecttemplates.',
					'jira.wikieditor.visibility',
				],
			},
			session: {
				exact: [
					'gh.pageloadmessages',
					'media-picker.herment.storage',
					'dismissedFlags',
					'AJS.thisPage',
					'selectedScreenTab',
					'issues-calendar-month',
					'jira.messages.fadeInBackground.color',
					'jira.messages.fadeInBackground.target',
					'selectedIssueId',
				],
			},
			indexeddb: {
				exact: [
					'jira-bsp__jira-issue-create-restore-in-progress-issue',
					'jira-bsp__jira-issue-create-restore-browser-storage-cleanup',
					'jpd-store',
					'jira-bsp__jpd-view-visits',
					'jira-bsp__cmp-board',
					'analytics-web-client',
					'atlassian-growth-recos-expiry',
				],
				startsWith: ['pendo-'],
			},
		},
	},
];
